type LocaleMessages = Record<string, string>;

export const SUPPORTED_LANGUAGES = {
  en: 'en',
  es: 'es',
  fr: 'fr_FR',
  de: 'de_DE',
  ja: 'ja_JP',
  'fr-CA': 'fr_CA',
  'pt-BR': 'pt_BR',
  'zh-CN': 'zh_CN',
  'zh-TW': 'zh_TW',
  'it-IT': 'it_IT',
  'nl-NL': 'nl_NL',
  'ko-KR': 'ko_KR',
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

const localeLoaders = Object.fromEntries(
  Object.entries(SUPPORTED_LANGUAGES)
    .filter(([key]) => key !== 'en')
    .map(([key, name]) => [
      key,
      () => import(`./compiled/${name}.json`).then((m) => m.default),
    ]),
) as Record<Exclude<SupportedLanguage, 'en'>, () => Promise<LocaleMessages>>;

// English strings are already bundled as defaultMessage values.
const englishMessages: LocaleMessages = {};
const cache = new Map<SupportedLanguage, LocaleMessages>([
  ['en', englishMessages],
]);

export const loadLocale = async (
  language: SupportedLanguage,
): Promise<LocaleMessages> => {
  const cached = cache.get(language);
  if (cached) {
    return cached;
  }

  const loader = localeLoaders[language as Exclude<SupportedLanguage, 'en'>];
  if (!loader) {
    return englishMessages;
  }

  const messages = await loader();
  cache.set(language, messages);
  return messages;
};

const getEnglishMessages = (): LocaleMessages => englishMessages;

export const getMessages = (language: SupportedLanguage): LocaleMessages =>
  cache.get(language) ?? getEnglishMessages();

export const isLocaleLoaded = (language: SupportedLanguage): boolean =>
  cache.has(language);
